import React, { useRef, useState } from "react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, X, ArrowUp, ArrowDown, Upload, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useLogoUrl, invalidateLogo } from "@/components/templates/useLogo";

const UITLIJNINGEN = [
  { v: "links", l: "Links" }, { v: "midden", l: "Gecentreerd" }, { v: "rechts", l: "Rechts" },
];

function UitlijningSelect({ value, onChange, testid }) {
  return (
    <Select value={value || "links"} onValueChange={onChange}>
      <SelectTrigger className="h-8 w-36 text-xs" data-testid={testid}><SelectValue /></SelectTrigger>
      <SelectContent>{UITLIJNINGEN.map((u) => <SelectItem key={u.v} value={u.v}>{u.l}</SelectItem>)}</SelectContent>
    </Select>
  );
}

function VeldSelect({ velden, value, onChange, testid }) {
  return (
    <Select value={value || undefined} onValueChange={onChange}>
      <SelectTrigger className="h-8 flex-1 text-xs" data-testid={testid}><SelectValue placeholder="Kies veld…" /></SelectTrigger>
      <SelectContent>
        {velden.map((v) => <SelectItem key={v.tag} value={v.tag}>{v.label}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}

/* Herbruikbare lijst van label+veld-paren (voor blokken 'velden' en 'totalen') */
function VeldItems({ items, velden, onChange, prefix }) {
  const update = (i, patch) => onChange(items.map((it, j) => (j === i ? { ...it, ...patch } : it)));
  const kiesVeld = (i, tag) => {
    const veld = velden.find((v) => v.tag === tag);
    const patch = { tag };
    if (!items[i].label && veld) patch.label = veld.label;
    update(i, patch);
  };
  return (
    <div className="space-y-1.5">
      {items.map((it, i) => (
        <div key={i} className="flex items-center gap-1.5">
          <Input className="h-8 w-40 text-xs" placeholder="Label" value={it.label || ""}
            onChange={(e) => update(i, { label: e.target.value })} data-testid={`${prefix}-label-${i}`} />
          <VeldSelect velden={velden} value={it.tag} onChange={(tag) => kiesVeld(i, tag)} testid={`${prefix}-veld-${i}`} />
          <Button variant="ghost" size="icon" className="h-7 w-7 shrink-0"
            onClick={() => onChange(items.filter((_, j) => j !== i))} data-testid={`${prefix}-verwijder-${i}`}>
            <X className="h-3.5 w-3.5" />
          </Button>
        </div>
      ))}
      <Button variant="outline" size="sm" className="h-7 text-xs"
        onClick={() => onChange([...items, { label: "", tag: "" }])} data-testid={`${prefix}-toevoegen`}>
        <Plus className="h-3 w-3 mr-1" /> Veld toevoegen
      </Button>
    </div>
  );
}

/* Logo-blok: breedte + uitlijning + upload van het bedrijfslogo */
function LogoBlok({ blok, set }) {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const logoUrl = useLogoUrl();

  const upload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!/\.(png|jpe?g)$/i.test(file.name)) { toast.error("Alleen .png of .jpg"); return; }
    const fd = new FormData();
    fd.append("file", file);
    setBusy(true);
    try {
      await api.post("/templates/logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Logo geüpload — geldt voor alle templates");
      invalidateLogo();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload mislukt");
    } finally { setBusy(false); }
  };

  const herstel = async () => {
    setBusy(true);
    try {
      await api.delete("/templates/logo");
      toast.success("Standaardlogo hersteld");
      invalidateLogo();
    } catch { toast.error("Herstellen mislukt"); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-2.5">
      <div className="flex items-center gap-3">
        <div className="border border-border rounded-md bg-white p-2 h-14 flex items-center">
          {logoUrl ? <img src={logoUrl} alt="Bedrijfslogo" className="max-h-10 max-w-[140px] object-contain" data-testid="blok-logo-afbeelding" />
            : <span className="text-xs text-muted-foreground px-2">Geen logo</span>}
        </div>
        <div className="flex flex-col gap-1.5">
          <input ref={fileRef} type="file" accept=".png,.jpg,.jpeg" className="hidden" onChange={upload} data-testid="blok-logo-bestand" />
          <Button variant="outline" size="sm" className="h-7 text-xs" disabled={busy}
            onClick={() => fileRef.current?.click()} data-testid="blok-logo-upload">
            <Upload className="h-3 w-3 mr-1" /> {busy ? "Bezig…" : "Logo uploaden"}
          </Button>
          <Button variant="ghost" size="sm" className="h-7 text-xs" disabled={busy} onClick={herstel} data-testid="blok-logo-herstel">
            <RotateCcw className="h-3 w-3 mr-1" /> Standaardlogo
          </Button>
        </div>
      </div>
      <div className="flex items-center gap-2">
        <label className="text-xs text-muted-foreground">Breedte (mm)</label>
        <Input type="number" min="10" max="180" className="h-8 w-20 text-xs" value={blok.breedte_mm ?? 46}
          onChange={(e) => set({ breedte_mm: Number(e.target.value) || 46 })} data-testid="blok-logo-breedte" />
        <UitlijningSelect value={blok.uitlijning} onChange={(v) => set({ uitlijning: v })} testid="blok-logo-uitlijning" />
      </div>
      <div className="text-[11px] text-muted-foreground">
        Het logo verschijnt altijd in de <strong>koptekst</strong> van elke pagina.
        Waar het logo-blok in de blokkenlijst staat maakt niet uit voor de plaatsing.
      </div>
    </div>
  );
}

export function BlokConfig({ blok, onChange, velden, tabelKolommen }) {
  const set = (patch) => onChange({ ...blok, ...patch });
  const adresVelden = velden.filter((v) => v.tag.endsWith("adres_blok"));

  if (blok.type === "logo") {
    return <LogoBlok blok={blok} set={set} />;
  }

  if (blok.type === "kop") {
    return (
      <div className="space-y-2">
        <Input className="h-8 text-sm" value={blok.tekst || ""} placeholder="Koptekst"
          onChange={(e) => set({ tekst: e.target.value })} data-testid="blok-kop-tekst" />
        <div className="flex gap-2 items-center flex-wrap">
          <UitlijningSelect value={blok.uitlijning} onChange={(v) => set({ uitlijning: v })} testid="blok-kop-uitlijning" />
          <label className="text-xs text-muted-foreground">Grootte</label>
          <Input type="number" min="8" max="96" className="h-8 w-20 text-xs" placeholder="29"
            value={blok.grootte_px ?? ""}
            onChange={(e) => set({ grootte_px: e.target.value ? Number(e.target.value) : null })}
            data-testid="blok-kop-grootte" />
          <span className="text-xs text-muted-foreground">px</span>
        </div>
      </div>
    );
  }

  if (blok.type === "tekst") {
    return (
      <div className="space-y-2">
        <Textarea className="text-sm min-h-[70px]" value={blok.tekst || ""} placeholder="Vrije tekst — gebruik velden via de knop hieronder"
          onChange={(e) => set({ tekst: e.target.value })} data-testid="blok-tekst-inhoud" />
        <div className="flex items-center gap-2 flex-wrap">
          <Select value={undefined} onValueChange={(tag) => set({ tekst: (blok.tekst || "") + `{{ ${tag} }}` })}>
            <SelectTrigger className="h-8 w-44 text-xs" data-testid="blok-tekst-veld-invoegen">
              <SelectValue placeholder="Veld invoegen…" />
            </SelectTrigger>
            <SelectContent>
              {velden.map((v) => <SelectItem key={v.tag} value={v.tag}>{v.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <UitlijningSelect value={blok.uitlijning} onChange={(v) => set({ uitlijning: v })} testid="blok-tekst-uitlijning" />
          <label className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <Switch checked={!!blok.vet} onCheckedChange={(v) => set({ vet: v })} data-testid="blok-tekst-vet" /> Vet
          </label>
        </div>
      </div>
    );
  }

  if (blok.type === "velden" || blok.type === "totalen") {
    return (
      <div className="space-y-2">
        {blok.type === "totalen" && (
          <div className="flex items-center gap-2">
            <label className="text-xs text-muted-foreground">Uitlijning</label>
            <UitlijningSelect value={blok.uitlijning ?? "rechts"} onChange={(v) => set({ uitlijning: v })} testid="blok-totalen-uitlijning" />
          </div>
        )}
        <VeldItems items={blok.items || []} velden={velden} prefix={`blok-${blok.type}`}
          onChange={(items) => set({ items })} />
      </div>
    );
  }

  if (blok.type === "kolommen") {
    const kolommen = blok.kolommen || [];
    const updateKol = (i, patch) => set({ kolommen: kolommen.map((k, j) => (j === i ? { ...k, ...patch } : k)) });
    return (
      <div className="space-y-2">
        {kolommen.map((kol, i) => (
          <div key={i} className="border border-border rounded-md p-2.5 space-y-2">
            <div className="flex items-center gap-1.5">
              <Input className="h-8 flex-1 text-xs" placeholder={`Titel kolom ${i + 1} (optioneel)`} value={kol.titel || ""}
                onChange={(e) => updateKol(i, { titel: e.target.value })} data-testid={`blok-kolom-titel-${i}`} />
              <Select value={kol.inhoud || "velden"} onValueChange={(v) => updateKol(i, { inhoud: v })}>
                <SelectTrigger className="h-8 w-32 text-xs" data-testid={`blok-kolom-inhoud-${i}`}><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="velden">Velden</SelectItem>
                  <SelectItem value="adres">Adresblok</SelectItem>
                </SelectContent>
              </Select>
              <Button variant="ghost" size="icon" className="h-7 w-7 shrink-0"
                onClick={() => set({ kolommen: kolommen.filter((_, j) => j !== i) })} data-testid={`blok-kolom-verwijder-${i}`}>
                <X className="h-3.5 w-3.5" />
              </Button>
            </div>
            {kol.inhoud === "adres" ? (
              <Select value={kol.adres_tag || "kantooradres_blok"} onValueChange={(v) => updateKol(i, { adres_tag: v })}>
                <SelectTrigger className="h-8 w-56 text-xs" data-testid={`blok-kolom-adres-${i}`}><SelectValue /></SelectTrigger>
                <SelectContent>
                  {adresVelden.map((v) => <SelectItem key={v.tag} value={v.tag}>{v.label}</SelectItem>)}
                </SelectContent>
              </Select>
            ) : (
              <VeldItems items={kol.velden || []} velden={velden} prefix={`blok-kolom-${i}`}
                onChange={(items) => updateKol(i, { velden: items })} />
            )}
          </div>
        ))}
        {kolommen.length < 3 && (
          <Button variant="outline" size="sm" className="h-7 text-xs"
            onClick={() => set({ kolommen: [...kolommen, { titel: "", inhoud: "velden", velden: [] }] })}
            data-testid="blok-kolom-toevoegen">
            <Plus className="h-3 w-3 mr-1" /> Kolom toevoegen (max 3)
          </Button>
        )}
      </div>
    );
  }

  if (blok.type === "regeltabel") {
    const kolommen = blok.kolommen || [];
    const beschikbaar = tabelKolommen.filter((tk) => !kolommen.some((k) => k.key === tk.key));
    const move = (i, d) => {
      const arr = [...kolommen];
      const j = i + d;
      if (j < 0 || j >= arr.length) return;
      [arr[i], arr[j]] = [arr[j], arr[i]];
      set({ kolommen: arr });
    };
    return (
      <div className="space-y-1.5">
        <div className="flex items-center gap-2 flex-wrap pb-1.5 border-b border-border mb-1.5">
          <Select value={blok.randen || "alle"} onValueChange={(v) => set({ randen: v })}>
            <SelectTrigger className="h-8 w-44 text-xs" data-testid="blok-tabel-randen"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="alle">Alle lijnen</SelectItem>
              <SelectItem value="horizontaal">Alleen horizontale lijnen</SelectItem>
              <SelectItem value="geen">Geen lijnen</SelectItem>
            </SelectContent>
          </Select>
          {(blok.randen || "alle") !== "geen" && (
            <label className="flex items-center gap-1.5 text-xs text-muted-foreground">
              Lijnkleur
              <input type="color" value={blok.rand_kleur || "#000000"}
                onChange={(e) => set({ rand_kleur: e.target.value })}
                className="h-7 w-9 rounded border border-border cursor-pointer bg-transparent"
                data-testid="blok-tabel-lijnkleur" />
            </label>
          )}
          <label className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <Switch checked={!!blok.kop_achtergrond}
              onCheckedChange={(v) => set({ kop_achtergrond: v ? "#F2F2F2" : "" })}
              data-testid="blok-tabel-kopbg-schakelaar" /> Kopregel-achtergrond
          </label>
          {blok.kop_achtergrond && (
            <input type="color" value={blok.kop_achtergrond}
              onChange={(e) => set({ kop_achtergrond: e.target.value })}
              className="h-7 w-9 rounded border border-border cursor-pointer bg-transparent"
              data-testid="blok-tabel-kopbg-kleur" />
          )}
        </div>
        {kolommen.map((k, i) => (
          <div key={k.key} className="flex items-center gap-1.5">
            <code className="text-[10px] bg-secondary rounded px-1.5 py-1 w-28 truncate shrink-0">r.{k.key}</code>
            <Input className="h-8 flex-1 text-xs" value={k.label || ""} placeholder="Kolomkop"
              onChange={(e) => set({ kolommen: kolommen.map((x, j) => (j === i ? { ...x, label: e.target.value } : x)) })}
              data-testid={`blok-tabel-label-${k.key}`} />
            <div className="flex items-center gap-1 shrink-0">
              <Input className="h-8 w-16 text-xs text-right" type="number" step="0.1" min="0" max="16"
                value={k.breedte_cm ?? ""}
                placeholder="auto"
                onChange={(e) => {
                  const v = e.target.value;
                  set({ kolommen: kolommen.map((x, j) => (j === i ? { ...x, breedte_cm: v === "" ? undefined : Number(v) } : x)) });
                }}
                title="Kolombreedte in cm (leeg = automatisch)"
                data-testid={`blok-tabel-breedte-${k.key}`} />
              <span className="text-[10px] text-muted-foreground">cm</span>
            </div>
            <Button variant="ghost" size="icon" className="h-7 w-7 shrink-0" onClick={() => move(i, -1)} data-testid={`blok-tabel-omhoog-${k.key}`}>
              <ArrowUp className="h-3.5 w-3.5" />
            </Button>
            <Button variant="ghost" size="icon" className="h-7 w-7 shrink-0" onClick={() => move(i, 1)} data-testid={`blok-tabel-omlaag-${k.key}`}>
              <ArrowDown className="h-3.5 w-3.5" />
            </Button>
            <Button variant="ghost" size="icon" className="h-7 w-7 shrink-0"
              onClick={() => set({ kolommen: kolommen.filter((_, j) => j !== i) })} data-testid={`blok-tabel-verwijder-${k.key}`}>
              <X className="h-3.5 w-3.5" />
            </Button>
          </div>
        ))}
        <div className="text-[11px] text-muted-foreground pt-1">
          Laat breedte leeg voor automatische verdeling. Totaal beschikbaar op A4: ± 16,5 cm.
        </div>
        {beschikbaar.length > 0 && (
          <Select value={undefined} onValueChange={(key) => {
            const tk = tabelKolommen.find((x) => x.key === key);
            set({ kolommen: [...kolommen, { key, label: tk?.label || key }] });
          }}>
            <SelectTrigger className="h-8 w-52 text-xs" data-testid="blok-tabel-kolom-toevoegen">
              <SelectValue placeholder="+ Kolom toevoegen…" />
            </SelectTrigger>
            <SelectContent>
              {beschikbaar.map((tk) => <SelectItem key={tk.key} value={tk.key}>{tk.label}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
      </div>
    );
  }

  if (blok.type === "spacer") {
    return <div className="text-xs text-muted-foreground">Witruimte tussen blokken.</div>;
  }

  if (blok.type === "footer") {
    const regels = blok.regels || ["", ""];
    return (
      <div className="space-y-1.5">
        {[0, 1].map((i) => (
          <Input key={i} className="h-8 text-xs" placeholder={`Footerregel ${i + 1}`} value={regels[i] || ""}
            onChange={(e) => {
              const nieuw = [...regels];
              nieuw[i] = e.target.value;
              set({ regels: nieuw });
            }} data-testid={`blok-footer-regel-${i}`} />
        ))}
        <div className="text-[11px] text-muted-foreground">Tip: gebruik {"{{ footer_bedrijf }}"} en {"{{ footer_contact }}"} voor je bedrijfsgegevens.</div>
      </div>
    );
  }

  return null;
}
